import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Gamepad2, 
  Zap, 
  Brain,
  Calculator,
  BookOpen,
  ArrowLeft,
  ArrowRight,
  Trophy,
  Coins,
  Clock,
  Star,
} from "lucide-react";
import { useSelector } from "react-redux";
import { UserModelData, PAGE_NAME } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";

interface Game {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  bgGradient: string;
  borderColor: string;
  reward: string;
  duration: string;
  difficulty: "Easy" | "Medium" | "Hard";
  route: PAGE_NAME;
}

const GamesPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const games: Game[] = [
    {
      id: "tap_rush",
      name: "Tap Rush",
      description: "Tap as fast as you can in 10 seconds! The more taps, the more ATH you earn.",
      icon: <Zap size={32} className="text-yellow-400" />,
      bgGradient: "from-yellow-500/20 to-orange-500/20",
      borderColor: "border-yellow-500/30",
      reward: "Up to 1 ATH",
      duration: "10 seconds",
      difficulty: "Easy",
      route: PAGE_NAME.TAP_RUSH,
    },
    {
      id: "memory_match",
      name: "Memory Match",
      description: "Match pairs of cards before time runs out. Test your memory skills!",
      icon: <Brain size={32} className="text-purple-400" />,
      bgGradient: "from-purple-500/20 to-pink-500/20",
      borderColor: "border-purple-500/30",
      reward: "Up to 2 ATH",
      duration: "60 seconds",
      difficulty: "Easy",
      route: PAGE_NAME.MEMORY_MATCH,
    },
    {
      id: "number_crunch",
      name: "Number Crunch",
      description: "Solve math problems as fast as possible! Difficulty increases as you go.",
      icon: <Calculator size={32} className="text-blue-400" />,
      bgGradient: "from-blue-500/20 to-cyan-500/20",
      borderColor: "border-blue-500/30",
      reward: "Up to 1.5 ATH",
      duration: "30 seconds",
      difficulty: "Medium",
      route: PAGE_NAME.NUMBER_CRUNCH,
    },
    {
      id: "crypto_quiz",
      name: "Crypto Quiz",
      description: "Test your crypto & blockchain knowledge with 10 trivia questions!",
      icon: <BookOpen size={32} className="text-emerald-400" />,
      bgGradient: "from-emerald-500/20 to-teal-500/20",
      borderColor: "border-emerald-500/30",
      reward: "Up to 2 ATH",
      duration: "No timer",
      difficulty: "Medium",
      route: PAGE_NAME.CRYPTO_QUIZ,
    },
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Easy": return "bg-emerald-500";
      case "Medium": return "bg-amber-500";
      case "Hard": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-slate-900" : "bg-gray-50"}`}>
      <CommonPageTitle title="Games" padding={null} />
      
      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => navigate(-1)}
            className={`p-2 rounded-xl ${theme === "dark" ? "bg-slate-800" : "bg-white"}`}
          >
            <ArrowLeft size={20} className={theme === "dark" ? "text-white" : "text-gray-900"} />
          </button>
          <div>
            <h1 className={`text-2xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              Game Arcade 🎮
            </h1>
            <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
              Play games, earn ATH rewards!
            </p>
          </div>
        </div>

        {/* Stats Banner */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className={`
            p-4 rounded-2xl mb-6 flex items-center justify-between
            ${theme === "dark" ? "bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border border-indigo-500/20" : "bg-gradient-to-r from-indigo-100 to-purple-100 border border-indigo-300"}
          `}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-indigo-500/20">
              <Trophy className="text-indigo-400" size={24} />
            </div>
            <div>
              <p className={`text-xs ${theme === "dark" ? "text-indigo-400" : "text-indigo-600"} font-bold uppercase`}>
                Your ATH Balance
              </p>
              <p className={`text-xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                {(user?.arthereumBalance || 0).toFixed(2)} ATH
              </p>
            </div>
          </div>
          <Star className="text-yellow-400" size={32} />
        </motion.div>

        {/* Info Banner */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`
            p-3 rounded-xl mb-6 flex items-center gap-3
            bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border border-emerald-500/30
          `}
        >
          <Coins size={20} className="text-emerald-400 shrink-0" />
          <p className={`text-xs ${theme === "dark" ? "text-emerald-300" : "text-emerald-600"}`}>
            <strong>Free to play!</strong> Win ATH with every game. No Pi required.
          </p>
        </motion.div>

        {/* Game Cards */}
        <div className="space-y-4">
          {games.map((game, idx) => (
            <motion.div
              key={game.id}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
              onClick={() => navigate(game.route)}
              className={`
                relative overflow-hidden rounded-2xl p-5 cursor-pointer
                bg-gradient-to-r ${game.bgGradient}
                border ${game.borderColor}
                ${theme === "dark" ? "bg-slate-800/80" : "bg-white/90"}
                hover:scale-[1.02] transition-transform active:scale-95
              `}
            >
              {/* Difficulty Badge */}
              <div className="absolute top-3 right-3">
                <span className={`px-2 py-1 rounded-full text-[10px] font-bold text-white ${getDifficultyColor(game.difficulty)}`}>
                  {game.difficulty}
                </span>
              </div>

              <div className="flex items-start gap-4">
                <motion.div 
                  className={`p-4 rounded-xl shrink-0 ${theme === "dark" ? "bg-slate-700/50" : "bg-white/50"}`}
                  whileHover={{ rotate: [0, -5, 5, 0] }}
                >
                  {game.icon}
                </motion.div>
                
                <div className="flex-1 min-w-0">
                  <h3 className={`font-bold text-lg ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {game.name}
                  </h3>
                  <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"} mb-3`}>
                    {game.description}
                  </p>
                  
                  {/* Game Stats */}
                  <div className="flex flex-wrap gap-2">
                    <span className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1 ${theme === "dark" ? "bg-emerald-900/50 text-emerald-400" : "bg-emerald-100 text-emerald-600"}`}>
                      <Coins size={12} /> {game.reward}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-lg flex items-center gap-1 ${theme === "dark" ? "bg-blue-900/50 text-blue-400" : "bg-blue-100 text-blue-600"}`}>
                      <Clock size={12} /> {game.duration}
                    </span>
                  </div>
                </div>
                
                <ArrowRight size={20} className={theme === "dark" ? "text-slate-500" : "text-gray-400"} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Coming Soon */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className={`
            mt-6 p-4 rounded-2xl text-center
            ${theme === "dark" ? "bg-slate-800/50 border border-slate-700 border-dashed" : "bg-white border border-gray-200 border-dashed"}
          `}
        >
          <Gamepad2 size={32} className={`mx-auto mb-2 ${theme === "dark" ? "text-slate-600" : "text-gray-400"}`} />
          <p className={`text-sm font-bold ${theme === "dark" ? "text-slate-500" : "text-gray-500"}`}>
            More games coming soon!
          </p>
          <p className={`text-xs ${theme === "dark" ? "text-slate-600" : "text-gray-400"}`}>
            Stay tuned for new ways to earn ATH
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default GamesPage;
